import { useEffect, useRef } from "react";
import { WORLD_DOTS } from "@/data/worldDots";

const CITIES = [
  { name: "New Delhi", lat: 28.61, lon: 77.21, hub: true },
  { name: "London", lat: 51.51, lon: -0.13 },
  { name: "Dubai", lat: 25.2, lon: 55.27 },
  { name: "Singapore", lat: 1.35, lon: 103.82 },
  { name: "New York", lat: 40.71, lon: -74.01 },
];

export default function WorldMap() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let w = 0;
    let h = 0;
    let raf;

    const resize = () => {
      w = wrap.clientWidth;
      h = Math.round(w * 0.52);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const proj = (lat, lon) => [((lon + 180) / 360) * w, ((90 - lat) / 180) * h];
    const hub = CITIES[0];
    const spokes = CITIES.slice(1).map((c, i) => {
      const [x1, y1] = proj(hub.lat, hub.lon);
      const [x2, y2] = proj(c.lat, c.lon);
      const mx = (x1 + x2) / 2;
      const my = Math.min(y1, y2) - Math.abs(x2 - x1) * 0.22 - 14;
      const pts = [];
      for (let s = 0; s <= 60; s++) {
        const t = s / 60;
        const inv = 1 - t;
        pts.push([
          inv * inv * x1 + 2 * inv * t * mx + t * t * x2,
          inv * inv * y1 + 2 * inv * t * my + t * t * y2,
        ]);
      }
      return { pts, phase: i * 0.27 };
    });

    const draw = (now) => {
      ctx.clearRect(0, 0, w, h);
      const t = now * 0.001;

      for (let i = 0; i < WORLD_DOTS.length; i++) {
        const [nx, ny] = WORLD_DOTS[i];
        const shimmer = 0.22 + 0.12 * Math.sin(t * 0.9 + i * 0.7);
        ctx.fillStyle = `rgba(148, 163, 184, ${shimmer})`;
        ctx.fillRect(nx * w, ny * h, 1.4, 1.4);
      }

      for (const { pts, phase } of spokes) {
        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        for (const [x, y] of pts) ctx.lineTo(x, y);
        ctx.strokeStyle = "rgba(212, 175, 55, 0.28)";
        ctx.lineWidth = 1;
        ctx.stroke();

        const tt = (t * 0.22 + phase) % 1;
        const idx = Math.floor(tt * (pts.length - 1));
        const [px, py] = pts[idx];
        const grad = ctx.createRadialGradient(px, py, 0, px, py, 7);
        grad.addColorStop(0, "rgba(243, 229, 171, 0.95)");
        grad.addColorStop(1, "rgba(212, 175, 55, 0)");
        ctx.beginPath();
        ctx.arc(px, py, 7, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      }

      ctx.textAlign = "left";
      for (const c of CITIES) {
        const [x, y] = proj(c.lat, c.lon);
        const cycle = (t % 2.4) / 2.4;
        ctx.beginPath();
        ctx.arc(x, y, 3 + cycle * 13, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(212, 175, 55, ${0.55 * (1 - cycle)})`;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y, c.hub ? 3.4 : 2.4, 0, Math.PI * 2);
        ctx.fillStyle = c.hub ? "#F3E5AB" : "#D4AF37";
        ctx.fill();
        ctx.font = "600 8px 'JetBrains Mono', monospace";
        ctx.fillStyle = c.hub ? "rgba(243, 229, 171, 0.95)" : "rgba(197, 160, 89, 0.85)";
        ctx.fillText(c.name.toUpperCase(), x + 7, y - 5);
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return (
    <div ref={wrapRef} data-testid="corridor-map" className="w-full min-w-0 overflow-hidden">
      <canvas ref={canvasRef} role="img" aria-label="Dotted world map with live corridors from New Delhi to London, Dubai, Singapore and New York" />
    </div>
  );
}
